export interface Quat {
  w: number
  x: number
  y: number
  z: number
}

export const INITIAL_SPHERE_QUAT: Quat = quatFromAxisAngle(1, 0, 0, 0.25)

export function quatFromAxisAngle(axisX: number, axisY: number, axisZ: number, angle: number): Quat {
  const half = angle / 2
  const sin = Math.sin(half)
  return {
    w: Math.cos(half),
    x: axisX * sin,
    y: axisY * sin,
    z: axisZ * sin,
  }
}

export function quatMultiply(a: Quat, b: Quat): Quat {
  return quatNormalize({
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
  })
}

export function quatNormalize(q: Quat): Quat {
  const length = Math.hypot(q.w, q.x, q.y, q.z)
  if (length === 0) {
    return { w: 1, x: 0, y: 0, z: 0 }
  }

  return {
    w: q.w / length,
    x: q.x / length,
    y: q.y / length,
    z: q.z / length,
  }
}

export function quatRotateVector(q: Quat, v: { x: number; y: number; z: number }) {
  const tx = 2 * (q.y * v.z - q.z * v.y)
  const ty = 2 * (q.z * v.x - q.x * v.z)
  const tz = 2 * (q.x * v.y - q.y * v.x)

  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  }
}

function screenPointToSphere(x: number, y: number) {
  const lengthSquared = x * x + y * y

  if (lengthSquared <= 1) {
    return { x, y, z: Math.sqrt(1 - lengthSquared) }
  }

  const inverseLength = 1 / Math.sqrt(lengthSquared)
  return { x: x * inverseLength, y: y * inverseLength, z: 0 }
}

function quatFromUnitVectors(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
): Quat {
  const dot = from.x * to.x + from.y * to.y + from.z * to.z

  if (dot > 0.999999) {
    return { w: 1, x: 0, y: 0, z: 0 }
  }

  if (dot < -0.999999) {
    return quatFromAxisAngle(0, 1, 0, Math.PI)
  }

  const crossX = from.y * to.z - from.z * to.y
  const crossY = from.z * to.x - from.x * to.z
  const crossZ = from.x * to.y - from.y * to.x

  return quatNormalize({
    w: 1 + dot,
    x: crossX,
    y: crossY,
    z: crossZ,
  })
}

/** Arcball rotation from a screen-space drag gesture. */
export function applyArcballDrag(
  start: Quat,
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
  width: number,
  height: number,
): Quat {
  if (width <= 0 || height <= 0) {
    return start
  }

  const radius = Math.min(width, height) / 2
  const centerX = width / 2
  const centerY = height / 2

  const toSphereCoords = (x: number, y: number) => ({
    x: (x - centerX) / radius,
    y: (y - centerY) / radius,
  })

  const startNormalized = toSphereCoords(startX, startY)
  const currentNormalized = toSphereCoords(currentX, currentY)
  const startPoint = screenPointToSphere(startNormalized.x, startNormalized.y)
  const currentPoint = screenPointToSphere(currentNormalized.x, currentNormalized.y)

  const dragRotation = quatFromUnitVectors(startPoint, currentPoint)
  return quatMultiply(dragRotation, start)
}

export function applyAutoRotationY(q: Quat, deltaRadians: number): Quat {
  const yaw = quatFromAxisAngle(0, 1, 0, deltaRadians)
  return quatMultiply(yaw, q)
}
